import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { API_URL } from './adminUtils';
import {
  Users,
  PenLine,
  CheckCircle2,
  Clock,
  Calendar,
  FileText,
  RefreshCw,
  CircleCheck,
} from 'lucide-react';

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, sigRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/signatures/status`)
      ]);
      setStats(statsRes.data);
      setSignatureStatus(sigRes.data);
    } catch (error) {
      // silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  if (loading) {
    return <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {Array(5).fill(0).map((_, i) => (
          <div key={i} className="h-32 bg-muted rounded-lg"></div>
        ))}
      </div>
    </div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Welcome to CCA Admin Portal</p>
        </div>
        <Button variant="outline" onClick={fetchData} size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-cameroon-green/10 to-cameroon-green/5 border-cameroon-green/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-cameroon-green/20">
                <Users className="w-6 h-6 text-cameroon-green" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Associations</p>
                <p className="text-3xl font-bold">{stats?.associations || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-cameroon-red/10 to-cameroon-red/5 border-cameroon-red/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-cameroon-red/20">
                <PenLine className="w-6 h-6 text-cameroon-red" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Signatures</p>
                <p className="text-3xl font-bold">{stats?.signatures || 0}<span className="text-lg text-muted-foreground">/{stats?.associations || 13}</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-cameroon-yellow/10 to-cameroon-yellow/5 border-cameroon-yellow/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-cameroon-yellow/20">
                <Clock className="w-6 h-6 text-cameroon-yellow" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Activities</p>
                <p className="text-3xl font-bold">{stats?.activities_pending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <CheckCircle2 className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-3xl font-bold">{stats?.activities_approved || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 border-indigo-500/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-indigo-500/20">
                <CircleCheck className="w-6 h-6 text-indigo-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-3xl font-bold">{stats?.activities_completed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Signature Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif flex items-center gap-2">
            <FileText className="w-5 h-5 text-cameroon-green" />
            Constitution Signing Progress
          </CardTitle>
          <CardDescription>
            {signatureStatus?.signed || 0} of {signatureStatus?.total || 12} associations have signed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="w-full bg-muted rounded-full h-4">
              <div 
                className="bg-gradient-to-r from-cameroon-green to-cameroon-green/80 h-4 rounded-full transition-all duration-500"
                style={{ width: `${(signatureStatus?.signed / signatureStatus?.total) * 100 || 0}%` }}
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Signed */}
            <div>
              <h4 className="text-sm font-semibold text-cameroon-green flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4" />
                Signed ({signatureStatus?.signed_associations?.length || 0})
              </h4>
              <div className="space-y-2">
                {signatureStatus?.signed_associations?.map((assoc) => (
                  <div 
                    key={assoc.association_id}
                    className="p-3 rounded-lg bg-cameroon-green/5 border border-cameroon-green/20 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-sm">{assoc.association_name}</p>
                      <p className="text-xs text-muted-foreground">by {assoc.president_name}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{new Date(assoc.signed_at).toLocaleDateString()}</p>
                  </div>
                ))}
                {signatureStatus?.signed_associations?.length === 0 && (
                  <p className="text-sm text-muted-foreground">No signatures yet</p>
                )}
              </div>
            </div>
            
            {/* Pending */}
            <div>
              <h4 className="text-sm font-semibold text-cameroon-yellow flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4" />
                Pending ({signatureStatus?.pending_associations?.length || 0})
              </h4>
              <div className="space-y-2">
                {signatureStatus?.pending_associations?.map((assoc) => (
                  <div 
                    key={assoc.association_id}
                    className="p-3 rounded-lg bg-muted/50 border flex items-center justify-between"
                  >
                    <p className="font-medium text-sm">{assoc.association_name}</p>
                    <Badge variant="outline" className="text-xs">Awaiting</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Link to="/admin/constitution">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold">Edit Constitution</p>
                <p className="text-sm text-muted-foreground">Modify content in EN/FR</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/admin/associations">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-cameroon-green/10">
                <Users className="w-6 h-6 text-cameroon-green" />
              </div>
              <div>
                <p className="font-semibold">Manage Associations</p>
                <p className="text-sm text-muted-foreground">Edit all 12 associations</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/admin/activities">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-cameroon-yellow/10">
                <Calendar className="w-6 h-6 text-cameroon-yellow" />
              </div>
              <div>
                <p className="font-semibold">Review Activities</p>
                <p className="text-sm text-muted-foreground">{stats?.activities_pending || 0} pending approval</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default DashboardOverview;
